import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Lock, FileText, Loader2, ArrowRight, ChevronLeft, Building2,
  CheckCircle2, Clock, RefreshCw, Download, MessageSquare, Send, X,
  LogOut, AlertCircle, ShieldCheck, Mail,
} from 'lucide-react';
import ExecutionPanel from '../../components/vdr/ExecutionPanel';
import SignaturePanel from '../../components/vdr/SignaturePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'vdr_token';
const EMAIL_KEY = 'vdr_email';

const fmtDateTime = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};

const ACTION_LABEL = {
  document_uploaded: 'Document uploaded',
  document_shared: 'Document shared',
  new_version_uploaded: 'New version uploaded',
  approvals_reset: 'Approvals reset',
  document_viewed: 'You opened this document',
  approval_given: 'You approved',
  changes_requested: 'You requested changes',
};

// Render reviewer instructions as a bullet list when multiple non-empty lines
// are present; fall back to plain text for a single-line message.
const ReviewerInstructionsBox = ({ text, testId }) => {
  const lines = (text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return null;
  return (
    <div
      className="mt-4 bg-amber-500/10 border-l-4 border-amber-400 rounded-lg p-4 flex items-start gap-3"
      data-testid={testId}
    >
      <div className="text-amber-300 mt-0.5 shrink-0" aria-hidden="true">ℹ️</div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide mb-1">
          Instructions from StartEase
        </p>
        {lines.length === 1 ? (
          <p className="text-sm text-amber-100">{lines[0]}</p>
        ) : (
          <ul className="text-sm text-amber-100 space-y-1 list-disc pl-5">
            {lines.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
};

const WhatsNewBanner = ({ note, versionNumber, testId }) => {
  const trimmed = (note || '').trim();
  if (!trimmed) return null;
  return (
    <div
      className="mt-4 bg-amber-500/10 border-l-4 border-amber-400 rounded-lg p-4 flex items-start gap-3"
      data-testid={testId}
    >
      <div className="text-amber-300 mt-0.5 shrink-0" aria-hidden="true">✨</div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide mb-1">
          What&apos;s new in v{versionNumber}
        </p>
        <p className="text-sm text-amber-100 whitespace-pre-wrap">{trimmed}</p>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const cfg = {
    draft: 'bg-gray-600/30 text-gray-300',
    shared: 'bg-blue-500/20 text-blue-300',
    final: 'bg-[#059669]/20 text-[#059669]',
  }[status] || 'bg-gray-600/30 text-gray-300';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg}`}>{status || '—'}</span>;
};

const apiAuth = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

// ─────────────────────────────────────────────────────────────────────────────
// Subtle marketing footer (every Party Portal screen)
// ─────────────────────────────────────────────────────────────────────────────
const BrandingFooter = () => (
  <footer className="mt-12 pt-6 border-t border-gray-800/80" data-testid="vdr-branding-footer">
    <div className="max-w-6xl mx-auto px-6 pb-6 flex flex-col sm:flex-row items-center justify-between gap-1.5 text-[11px] text-gray-500">
      <p className="tracking-wide">
        Powered by{' '}
        <a
          href="https://starteaseai.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-400 hover:text-[#059669] transition-colors"
          data-testid="vdr-branding-link"
        >
          StartEase Agent
        </a>
        {' · US entity, tax & compliance — done for you'}
      </p>
      <a
        href="https://starteaseai.com"
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-600 hover:text-[#059669] transition-colors"
        data-testid="vdr-branding-url"
      >
        starteaseai.com
      </a>
    </div>
  </footer>
);

// ─────────────────────────────────────────────────────────────────────────────
// Post-approval marketing nudge (only after `approved`, never `changes_requested`)
// ─────────────────────────────────────────────────────────────────────────────
const PostApprovalNudge = ({ testId }) => (
  <div
    className="mt-4 bg-[#0b1310] border border-[#059669]/30 rounded-lg p-4"
    data-testid={testId}
  >
    <p className="text-sm font-semibold text-white">Need to form your own US company?</p>
    <p className="text-xs text-gray-400 mt-1 leading-relaxed">
      StartEase handles everything — LLC, C-Corp, EIN, Compliance.
    </p>
    <a
      href="https://starteaseai.com"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-[#059669]/15 hover:bg-[#059669]/25 text-[#059669] rounded-md text-xs font-semibold transition-colors"
      data-testid={`${testId}-cta`}
    >
      Learn More <ArrowRight className="w-3.5 h-3.5" />
    </a>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Branded shell — same dark palette as platform
// ─────────────────────────────────────────────────────────────────────────────
const Shell = ({ children, email, onLogout, showBack, onBack }) => (
  <div className="min-h-screen flex flex-col bg-[#0b1310] text-white">
    <header className="border-b border-gray-800 bg-[#0b1310]">
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showBack && (
            <button onClick={onBack} className="text-gray-400 hover:text-white p-1 -ml-1" data-testid="vdr-back-btn">
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <a
            href="https://starteaseai.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="vdr-brand-home-link"
            title="Visit StartEase Agent"
          >
            <ShieldCheck className="w-5 h-5 text-[#059669]" />
            <span className="text-sm font-semibold tracking-wide">STARTEASE · DATA ROOM</span>
          </a>
        </div>
        {email && (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-gray-400 hidden sm:inline">{email}</span>
            <button onClick={onLogout} className="text-gray-400 hover:text-white inline-flex items-center gap-1.5 text-xs" data-testid="vdr-logout-btn">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        )}
      </div>
    </header>
    <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-8">{children}</main>
    <BrandingFooter />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Screen 1: Login (email → OTP)
// ─────────────────────────────────────────────────────────────────────────────
const VdrLoginScreen = ({ onLoggedIn }) => {
  const [stage, setStage] = useState('email'); // email | otp
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const sendOtp = async (e) => {
    e?.preventDefault();
    setError('');
    if (!email.trim()) return setError('Please enter your email');
    setSubmitting(true);
    try {
      await axios.post(`${API}/vdr/auth/request-otp`, { email: email.trim().toLowerCase() });
      setStage('otp');
      setResendTimer(30);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send code. Please try again.');
    } finally { setSubmitting(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) return setError('Enter the 6-digit code');
    setError('');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/vdr/auth/verify-otp`, {
        email: email.trim().toLowerCase(), otp: code,
      });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      localStorage.setItem(EMAIL_KEY, email.trim().toLowerCase());
      onLoggedIn(email.trim().toLowerCase());
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code');
      setSubmitting(false);
    }
  };

  const onOtpChange = (i, v) => {
    const ch = v.replace(/\D/g, '').slice(0, 1);
    const next = [...otp]; next[i] = ch; setOtp(next);
    if (ch && i < 5) otpRefs.current[i + 1]?.focus();
  };
  const onOtpPaste = (e) => {
    const txt = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (txt.length === 6) {
      e.preventDefault();
      setOtp(txt.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  return (
    <Shell>
      <div className="max-w-md mx-auto mt-12" data-testid="vdr-login-screen">
        <div className="bg-[#111827] border border-gray-700 rounded-2xl p-8">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-[#059669]/15 border border-[#059669]/30 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#059669]" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-center mb-2">Sign in to your Data Room</h1>
          <p className="text-sm text-gray-400 text-center mb-6">
            {stage === 'email'
              ? 'Enter the email address that was invited.'
              : `We sent a 6-digit code to ${email}`}
          </p>

          {stage === 'email' ? (
            <form onSubmit={sendOtp} className="space-y-4" data-testid="vdr-login-email-form">
              <div>
                <label className="block text-sm text-gray-300 mb-1.5">Email</label>
                <input
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full bg-[#0b1310] border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-[#059669] focus:outline-none"
                  data-testid="vdr-login-email-input"
                />
              </div>
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-400 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}
              <button type="submit" disabled={submitting}
                      className="w-full bg-[#059669] hover:bg-[#00d96e] text-black rounded-lg py-2.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="vdr-login-send-otp">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Send sign-in code <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-gray-500 text-center pt-2">
                No password — we email a one-time code for every sign-in.
              </p>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-4" data-testid="vdr-login-otp-form">
              <div className="flex gap-2 justify-center" onPaste={onOtpPaste}>
                {otp.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => onOtpChange(i, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Backspace' && !d && i > 0) otpRefs.current[i - 1]?.focus(); }}
                    className="w-12 h-12 text-center text-lg font-mono bg-[#0b1310] border border-gray-700 rounded-lg text-white focus:border-[#059669] focus:outline-none"
                    data-testid={`vdr-otp-input-${i}`}
                  />
                ))}
              </div>
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-400 text-center">{error}</div>
              )}
              <button type="submit" disabled={submitting}
                      className="w-full bg-[#059669] hover:bg-[#00d96e] text-black rounded-lg py-2.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="vdr-verify-otp">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Verify and continue
              </button>
              <div className="flex items-center justify-between text-xs text-gray-400 pt-1">
                <button type="button" onClick={() => { setStage('email'); setOtp(['', '', '', '', '', '']); setError(''); }} className="hover:text-white">
                  Use a different email
                </button>
                {resendTimer > 0 ? (
                  <span>Resend in {resendTimer}s</span>
                ) : (
                  <button type="button" onClick={sendOtp} className="hover:text-white">Resend code</button>
                )}
              </div>
            </form>
          )}
        </div>
        <p className="text-xs text-gray-500 text-center mt-4">
          By signing in you agree to receive transactional emails about documents shared with you.
        </p>

        {stage === 'email' && (
          <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto" data-testid="vdr-login-trust-bar">
            <div className="bg-[#0b1310] border border-gray-800 rounded-lg p-3.5 flex items-start gap-2.5">
              <Lock className="w-4 h-4 text-[#059669] mt-0.5 shrink-0" strokeWidth={1.8} />
              <div>
                <p className="text-xs font-semibold text-white">OTP login</p>
                <p className="text-[11px] text-gray-500 leading-snug mt-0.5">No password to remember.</p>
              </div>
            </div>
            <div className="bg-[#0b1310] border border-gray-800 rounded-lg p-3.5 flex items-start gap-2.5">
              <ShieldCheck className="w-4 h-4 text-[#059669] mt-0.5 shrink-0" strokeWidth={1.8} />
              <div>
                <p className="text-xs font-semibold text-white">Encrypted</p>
                <p className="text-[11px] text-gray-500 leading-snug mt-0.5">AWS S3 with expiring URLs.</p>
              </div>
            </div>
            <div className="bg-[#0b1310] border border-gray-800 rounded-lg p-3.5 flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-[#059669] mt-0.5 shrink-0" strokeWidth={1.8} />
              <div>
                <p className="text-xs font-semibold text-white">Audit trail</p>
                <p className="text-[11px] text-gray-500 leading-snug mt-0.5">Every action timestamped.</p>
              </div>
            </div>
          </div>
        )}

        {stage === 'email' && (
          <div
            className="mt-6 max-w-2xl mx-auto bg-gradient-to-br from-[#0b1310] to-[#101820] border border-[#059669]/20 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            data-testid="vdr-login-discovery-card"
          >
            <div>
              <p className="text-sm font-semibold text-white">New to StartEase Data Room?</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                See how founders share deal documents with their investors — securely, with a full audit trail.
              </p>
            </div>
            <a
              href="https://starteaseai.com/data-room"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-1.5 bg-[#059669]/15 hover:bg-[#059669]/25 text-[#059669] rounded-md px-3.5 py-2 text-xs font-semibold transition-colors"
              data-testid="vdr-login-discovery-cta"
            >
              Learn More <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        )}
      </div>
    </Shell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Screen 2: Company selection
// ─────────────────────────────────────────────────────────────────────────────
const CompanyPickerScreen = ({ email, onPick, onLogout }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/vdr/companies`, { headers: apiAuth() });
        setCompanies(r.data || []);
      } catch (e) {
        if (e.response?.status === 401 || e.response?.status === 403) {
          localStorage.removeItem(TOKEN_KEY);
          window.location.reload();
          return;
        }
        setError(e.response?.data?.detail || 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, []);

  // Auto-pick when only one company
  useEffect(() => {
    if (!loading && companies.length === 1) onPick(companies[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, companies]);

  return (
    <Shell email={email} onLogout={onLogout}>
      <h1 className="text-2xl font-semibold mb-1">Choose a Data Room</h1>
      <p className="text-sm text-gray-400 mb-8">You have access to the following Data Rooms.</p>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading...
        </div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">{error}</div>
      ) : companies.length === 0 ? (
        <div className="bg-[#111827] border border-gray-700 rounded-xl py-16 text-center">
          <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-300 font-medium">You don&apos;t have access to any Data Rooms.</p>
          <p className="text-gray-500 text-sm mt-1">If you&apos;re expecting access, please reach out to whoever invited you.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="vdr-company-picker">
          {companies.map((c) => (
            <button
              key={c.company_id}
              onClick={() => onPick(c)}
              className="text-left bg-[#111827] hover:bg-[#1c2230] border border-gray-700 hover:border-[#059669]/40 rounded-xl p-5 transition-colors"
              data-testid={`vdr-company-card-${c.company_id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#059669]/15 flex items-center justify-center text-[#059669]">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-white">{c.company_name}</p>
                  <p className="text-xs text-gray-500">Data Room</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">{c.document_count} document{c.document_count !== 1 ? 's' : ''}</span>
                {c.pending_approvals > 0 ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-medium">
                    <Clock className="w-3 h-3" /> {c.pending_approvals} awaiting your response
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[#059669]">
                    <CheckCircle2 className="w-3 h-3" /> All caught up
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </Shell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Screen 3: Document list
// ─────────────────────────────────────────────────────────────────────────────
const DocumentListScreen = ({ company, email, onOpen, onBack, onLogout }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDocs = async () => {
    try {
      const r = await axios.get(`${API}/vdr/${company.company_id}/documents`, { headers: apiAuth() });
      setDocs(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load documents');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchDocs(); }, []); // eslint-disable-line

  const statusOf = (s) => ({ approved: { icon: CheckCircle2, color: 'text-[#059669]', label: 'You approved' },
    changes_requested: { icon: RefreshCw, color: 'text-orange-400', label: 'Changes requested' },
    pending: { icon: Clock, color: 'text-amber-300', label: 'Action required' },
  }[s] || { icon: Clock, color: 'text-gray-400', label: 'Pending' });

  return (
    <Shell email={email} onLogout={onLogout} showBack onBack={onBack}>
      <div className="mb-6">
        <p className="text-xs text-gray-400 uppercase tracking-wider">Data Room</p>
        <h1 className="text-2xl font-semibold mt-1">{company.company_name}</h1>
        <p className="text-sm text-gray-400 mt-1">{docs.length} document{docs.length !== 1 ? 's' : ''} shared with you</p>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">{error}</div>
      ) : docs.length === 0 ? (
        <div className="bg-[#111827] border border-gray-700 rounded-xl py-16 text-center">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-300 font-medium">No documents yet.</p>
          <p className="text-gray-500 text-sm mt-1">You&apos;ll see documents here once they&apos;re shared with you.</p>
        </div>
      ) : (
        <div className="bg-[#111827] border border-gray-700 rounded-xl overflow-hidden" data-testid="vdr-doc-list">
          <table className="w-full text-sm">
            <thead className="bg-[#0b1310] border-b border-gray-700">
              <tr>
                <th className="text-left px-5 py-3 text-gray-400 font-medium">Document</th>
                <th className="text-left px-5 py-3 text-gray-400 font-medium">Version</th>
                <th className="text-left px-5 py-3 text-gray-400 font-medium">Status</th>
                <th className="text-left px-5 py-3 text-gray-400 font-medium">Your response</th>
                <th className="text-left px-5 py-3 text-gray-400 font-medium">Comments</th>
                <th className="text-right px-5 py-3 text-gray-400 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/60">
              {docs.map((d) => {
                const st = statusOf(d.your_approval_status);
                const Icon = st.icon;
                return (
                  <tr key={d.id} className="hover:bg-white/5 cursor-pointer" onClick={() => onOpen(d.id)} data-testid={`vdr-doc-row-${d.id}`}>
                    <td className="px-5 py-3 text-white">{d.title}</td>
                    <td className="px-5 py-3 text-gray-300 text-xs font-mono">v{d.current_version_number}</td>
                    <td className="px-5 py-3"><StatusPill status={d.status} /></td>
                    <td className={`px-5 py-3 inline-flex items-center gap-1.5 ${st.color}`}>
                      <Icon className="w-4 h-4" />
                      <span className="text-xs font-medium">{st.label}</span>
                    </td>
                    <td className="px-5 py-3 text-gray-400 text-xs flex items-center gap-1">
                      {d.comment_count > 0 && <MessageSquare className="w-3.5 h-3.5" />}
                      {d.comment_count}
                    </td>
                    <td className="px-5 py-3 text-right text-[#059669] text-xs font-medium">Open →</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Shell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Screen 4: Document detail with approval actions + comments
// ─────────────────────────────────────────────────────────────────────────────
const DocumentDetailScreen = ({ company, docId, email, onBack, onLogout, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showChangesForm, setShowChangesForm] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionErr, setActionErr] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [newComment, setNewComment] = useState('');

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/vdr/${company.company_id}/documents/${docId}`, { headers: apiAuth() });
      setDoc(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load document');
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [docId]); // eslint-disable-line

  const openFresh = async () => {
    try {
      const r = await axios.get(`${API}/vdr/${company.company_id}/documents/${docId}`, { headers: apiAuth() });
      setDoc(r.data);
      const url = r.data?.current_version?.presigned_url;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) { /* ignored — caller will see panel error on next refresh */ }
  };

  const yourApproval = doc?.your_approval;
  const isLocked = yourApproval && yourApproval.status !== 'pending';

  const submitApproval = async (status, commentText) => {
    setActionErr(''); setSubmitting(true);
    try {
      await axios.post(`${API}/vdr/${company.company_id}/documents/${docId}/approve`,
        { status, comment_text: commentText || null },
        { headers: apiAuth() });
      setShowChangesForm(false);
      setComment('');
      await refresh();
      onChanged?.();
    } catch (e) {
      setActionErr(e.response?.data?.detail || 'Could not submit your response.');
    } finally { setSubmitting(false); }
  };

  const postComment = async () => {
    if (!newComment.trim() || postingComment) return;
    setPostingComment(true);
    try {
      await axios.post(`${API}/vdr/${company.company_id}/documents/${docId}/comments`,
        { comment_text: newComment.trim() }, { headers: apiAuth() });
      setNewComment('');
      await refresh();
    } finally { setPostingComment(false); }
  };

  return (
    <Shell email={email} onLogout={onLogout} showBack onBack={onBack}>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">{error}</div>
      ) : doc && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left — document header + activity */}
          <div className="lg:col-span-3 space-y-6">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">{company.company_name}</p>
              <h1 className="text-2xl font-semibold mt-1">{doc.title}</h1>
              {doc.description && <p className="text-gray-400 text-sm mt-1.5">{doc.description}</p>}
              <WhatsNewBanner note={doc.current_change_note} versionNumber={doc.current_version_number} testId="vdr-whats-new" />
              <ReviewerInstructionsBox text={doc.reviewer_instructions} testId="vdr-reviewer-instructions" />
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <StatusPill status={doc.status} />
                <span className="text-xs text-gray-300">Version {doc.current_version_number}</span>
                {doc.current_version && (
                  <button type="button" onClick={openFresh}
                     className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#059669]/15 hover:bg-[#059669]/25 text-[#059669] rounded-lg text-xs font-medium"
                     data-testid="vdr-download-current">
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </button>
                )}
              </div>
            </div>

            {/* Sign Digitally stage (visible only after all approved, before physical/exec) */}
            <SignaturePanel
              doc={doc}
              docEndpointBase={`${API}/vdr/${company.company_id}/documents/${docId}`}
              authHeaders={apiAuth()}
              onRefresh={refresh}
              testIdPrefix="vdr"
            />

            {/* Execute Agreement (visible once all parties approve) */}
            <ExecutionPanel
              doc={doc}
              docEndpointBase={`${API}/vdr/${company.company_id}/documents/${docId}`}
              authHeaders={apiAuth()}
              onRefresh={refresh}
              testIdPrefix="vdr"
              canExecute={true}
            />

            {/* Activity */}
            <div className="bg-[#111827] border border-gray-700 rounded-xl p-5">
              <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4" /> Discussion & Activity
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {(doc.activity || []).length === 0 && (
                  <p className="text-xs text-gray-500">No activity yet.</p>
                )}
                {(doc.activity || []).map((e) => {
                  const isComment = e.entry_type === 'comment';
                  if (isComment) {
                    return (
                      <div key={e.id} className="border-l-2 border-[#059669]/30 pl-3">
                        <div className="text-xs text-gray-400">
                          <span className="text-white font-medium">{e.performed_by_name || e.performed_by_email}</span>
                          {' · '}{fmtDateTime(e.created_at)}
                          {' · '}<span className="font-mono">v{e.version_number}</span>
                        </div>
                        <p className="text-sm text-gray-100 mt-1 whitespace-pre-wrap">{e.comment_text}</p>
                      </div>
                    );
                  }
                  const isReset = e.action === 'approvals_reset';
                  const isVer = e.action === 'new_version_uploaded';
                  const label = ACTION_LABEL[e.action] || e.action;
                  return (
                    <div key={e.id} className="text-xs text-gray-500 italic">
                      <span className="font-mono not-italic mr-1.5">v{e.version_number}</span>
                      {isReset ? <span>── All approvals reset ──</span>
                       : isVer ? <span>Version {e.version_number} uploaded · {fmtDateTime(e.created_at)}</span>
                       : <span>{label} · {fmtDateTime(e.created_at)}</span>}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex gap-2">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={2}
                  placeholder="Add a comment..."
                  className="flex-1 bg-[#0b1310] border border-gray-700 rounded-lg px-3 py-2 text-sm focus:border-[#059669] focus:outline-none"
                  data-testid="vdr-comment-input"
                />
                <button onClick={postComment} disabled={!newComment.trim() || postingComment}
                        className="self-end px-3 py-2 bg-[#059669] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5"
                        data-testid="vdr-post-comment">
                  {postingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Post
                </button>
              </div>
            </div>
          </div>

          {/* Right — your response */}
          <div className="lg:col-span-2">
            <div className="bg-[#111827] border border-gray-700 rounded-xl p-5 sticky top-6" data-testid="vdr-your-response-card">
              <h3 className="text-sm font-medium text-white mb-1">Your response</h3>
              <p className="text-xs text-gray-500 mb-4">on Version {doc.current_version_number}</p>

              {!yourApproval ? (
                <p className="text-xs text-gray-500">You&apos;re not listed as a reviewer for this document.</p>
              ) : isLocked ? (
                <div data-testid="vdr-locked-state">
                  {yourApproval.status === 'approved' ? (
                    <>
                      <div className="flex flex-col items-center text-center py-3">
                        <div className="w-14 h-14 rounded-full bg-[#059669]/15 border-2 border-[#059669] flex items-center justify-center mb-3">
                          <CheckCircle2 className="w-7 h-7 text-[#059669]" />
                        </div>
                        <p className="text-base font-semibold text-[#059669]">You approved this version</p>
                        <p className="text-xs text-gray-400 mt-1">{fmtDateTime(yourApproval.responded_at)}</p>
                      </div>
                      <PostApprovalNudge testId="vdr-post-approval-nudge" />
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-center py-3">
                      <div className="w-14 h-14 rounded-full bg-orange-500/15 border-2 border-orange-400 flex items-center justify-center mb-3">
                        <RefreshCw className="w-7 h-7 text-orange-400" />
                      </div>
                      <p className="text-base font-semibold text-orange-400">You requested changes</p>
                      <p className="text-xs text-gray-400 mt-1">{fmtDateTime(yourApproval.responded_at)}</p>
                      {yourApproval.comment_text && (
                        <div className="mt-3 text-xs text-orange-300 bg-orange-500/10 rounded p-2.5 italic w-full text-left">&ldquo;{yourApproval.comment_text}&rdquo;</div>
                      )}
                    </div>
                  )}
                  <div className="mt-4 pt-3 border-t border-gray-700/50">
                    <p className="text-xs text-gray-500 text-center">
                      <Lock className="w-3 h-3 inline mr-1" />
                      Your response is locked. A new version will reset all approvals.
                    </p>
                  </div>
                </div>
              ) : showChangesForm ? (
                <div data-testid="vdr-changes-form">
                  <p className="text-sm text-gray-300 mb-2">Tell the team what needs to change:</p>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={5}
                    autoFocus
                    placeholder="Please describe what needs to be updated..."
                    className="w-full bg-[#0b1310] border border-gray-700 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                    data-testid="vdr-changes-textarea"
                  />
                  {actionErr && <div className="text-xs text-red-400 mt-2 flex items-start gap-1.5"><AlertCircle className="w-3.5 h-3.5 mt-0.5" /> {actionErr}</div>}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => { setShowChangesForm(false); setComment(''); setActionErr(''); }}
                            className="flex-1 py-2 text-sm text-gray-300 hover:text-white">
                      Cancel
                    </button>
                    <button onClick={() => submitApproval('changes_requested', comment.trim())}
                            disabled={!comment.trim() || submitting}
                            className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                            data-testid="vdr-submit-changes">
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      Submit
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-center py-2">
                    <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-400/40 flex items-center justify-center mx-auto mb-2">
                      <Clock className="w-7 h-7 text-amber-300" />
                    </div>
                    <p className="text-sm font-medium text-amber-300">Awaiting your response</p>
                    <p className="text-xs text-gray-400 mt-1">Read the document and choose:</p>
                  </div>
                  {actionErr && <div className="text-xs text-red-400 flex items-start gap-1.5"><AlertCircle className="w-3.5 h-3.5 mt-0.5" /> {actionErr}</div>}
                  <button onClick={() => submitApproval('approved')}
                          disabled={submitting}
                          className="w-full py-2.5 bg-[#059669] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                          data-testid="vdr-approve-btn">
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    <CheckCircle2 className="w-4 h-4" /> Approve
                  </button>
                  <button onClick={() => setShowChangesForm(true)}
                          className="w-full py-2.5 bg-white/5 hover:bg-white/10 border border-orange-400/40 text-orange-300 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
                          data-testid="vdr-request-changes-btn">
                    <RefreshCw className="w-4 h-4" /> Request Changes
                  </button>
                  <p className="text-xs text-gray-500 text-center pt-1">
                    Your decision is recorded and emailed to the document owner.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Top-level controller
// ─────────────────────────────────────────────────────────────────────────────
const VdrPortal = () => {
  const [stage, setStage] = useState(() => (localStorage.getItem(TOKEN_KEY) ? 'picker' : 'login'));
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) || '');
  const [company, setCompany] = useState(null);
  const [openDocId, setOpenDocId] = useState(null);

  const logout = async () => {
    try { await axios.post(`${API}/vdr/auth/logout`, {}, { headers: apiAuth() }); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setStage('login');
    setEmail(''); setCompany(null); setOpenDocId(null);
  };

  if (stage === 'login') {
    return <VdrLoginScreen onLoggedIn={(e) => { setEmail(e); setStage('picker'); }} />;
  }
  if (stage === 'picker') {
    return <CompanyPickerScreen email={email} onLogout={logout}
              onPick={(c) => { setCompany(c); setStage('list'); }} />;
  }
  if (stage === 'list' && company) {
    return <DocumentListScreen company={company} email={email} onLogout={logout}
              onBack={() => { setCompany(null); setStage('picker'); }}
              onOpen={(id) => { setOpenDocId(id); setStage('detail'); }} />;
  }
  if (stage === 'detail' && company && openDocId) {
    return <DocumentDetailScreen company={company} docId={openDocId} email={email}
              onLogout={logout}
              onBack={() => { setOpenDocId(null); setStage('list'); }} />;
  }
  return null;
};

export default VdrPortal;
